import { Fragment, useMemo, type ReactNode } from "react";
import { mentionNodes } from "@/components/mention-text";
import { parseBlocks, taskMarks, type Block, type TaskMark } from "@/lib/markdown-blocks";
import { cn } from "@/lib/utils";

/**
 * Markdown for the comment thread, and for a card's description.
 *
 * Claude writes markdown — headings, bullets, `code`, fenced blocks — and until
 * now the thread rendered it as literal asterisks and backticks, which is worst
 * exactly where it matters most: a run reporting what it changed writes a list of
 * files, and a list of files is the thing that turned into a wall.
 *
 * Written here rather than pulled in as a dependency for one reason that is not
 * taste: mention highlighting has to happen *inside* the text nodes, and the
 * regex doing it must stay the single copy that agrees with core's parser (see
 * mention-text.tsx). Routing that through a general markdown pipeline's node
 * overrides means re-splitting its children anyway, so the subset is parsed here
 * and `mentionNodes` is called on every run of plain text.
 *
 * The subset is what an agent writing a status update actually uses: headings,
 * ordered and unordered lists (nested), task lists, fenced and inline code,
 * bold, italic, strikethrough, links, block quotes, tables and rules. Anything
 * outside it survives as its own text rather than disappearing — an unclosed
 * fence is rendered as a code block, not swallowed. The block parse is
 * `lib/markdown-blocks.ts`, shared with the checkbox toggle.
 *
 * No raw HTML, ever: nothing here uses `dangerouslySetInnerHTML`, so a `<script>`
 * in a body is text React escapes, and a link's href is one of `safeHref`'s
 * schemes or the link stays text.
 */

/* ------------------------------------------------------------------- inline */

/**
 * One pass over the alternation below. Code spans come first in the pattern so
 * everything inside a backtick is left alone — `**kwargs` in a comment about
 * Python is not bold. An underscore inside a word is not emphasis either (CommonMark's
 * rule), or `tsk_abc … agt_x` italicised across a whole paragraph and ate the backticks between.
 * Nor is an asterisk with a space on its inner side (CommonMark's flanking rule): people's
 * comments render here too, and `5 * 3 and 2 * 4` is arithmetic, not an italic ` 3 and 2 `.
 */
const INLINE =
  /(`+)([\s\S]*?)\1|\*\*(?!\s)([\s\S]+?)(?<!\s)\*\*|(?<![\p{L}\p{N}_])__([\s\S]+?)__(?![\p{L}\p{N}_])|~~([\s\S]+?)~~|\*(?!\s)([^*\n]+?)(?<!\s)\*|(?<![\p{L}\p{N}_])_([^_\n]+?)_(?![\p{L}\p{N}_])|\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)|<(https?:\/\/[^>\s]+)>|(https?:\/\/[^\s<>()[\]]+[^\s<>()[\].,;:!?'"])/gu;

/** Only schemes that cannot execute anything if the link is clicked. */
function safeHref(href: string): string | null {
  const trimmed = href.trim();
  return /^(https?:\/\/|mailto:)/i.test(trimmed) ? trimmed : null;
}

/** For text shown where no agent handles are known: nothing is highlighted as a mention. */
export const NO_HANDLES = new Set<string>();

// A chip that wraps keeps its border and padding on every line (clone, with the
// prefix Safari still needs), and a long path breaks anywhere rather than
// running out of its box.
const codeClass =
  "rounded border border-border/70 bg-muted px-1 py-px font-mono text-[0.9em] text-card-foreground " +
  "[-webkit-box-decoration-break:clone] [box-decoration-break:clone] [overflow-wrap:anywhere]";

function InlineLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  );
}

/**
 * Inline markdown for one run of text, with soft line breaks preserved. Claude
 * writes a status update the way it writes a message — one fact per line, no
 * blank line between them — and collapsing those into a paragraph the way strict
 * markdown does would undo the formatting rather than apply it.
 */
function inline(text: string, handles: Set<string>, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let cursor = 0;
  let key = 0;

  const plain = (value: string) => {
    if (!value) return;
    // Soft breaks are rendered, so a list of files stays a list of files.
    const segments = value.split("\n");
    segments.forEach((segment, index) => {
      if (index > 0) out.push(<br key={`${keyPrefix}-br-${key++}`} />);
      if (segment) out.push(<Fragment key={`${keyPrefix}-t-${key++}`}>{mentionNodes(segment, handles, `${keyPrefix}-${key}`)}</Fragment>);
    });
  };

  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0;
    const [whole, , code, strongStar, strongUnderscore, strike, emStar, emUnderscore, linkText, linkHref, autoAngle, autoBare] =
      match;

    // Emphasis on an underscore only counts between non-word characters, or
    // every snake_case identifier in a comment about code comes out italic.
    if (emUnderscore !== undefined) {
      const before = text[at - 1] ?? " ";
      const after = text[at + whole.length] ?? " ";
      if (/\w/.test(before) || /\w/.test(after)) continue;
    }

    plain(text.slice(cursor, at));
    cursor = at + whole.length;

    if (code !== undefined) {
      out.push(
        <code key={`${keyPrefix}-c-${key++}`} className={codeClass}>
          {code.replace(/^ (.*) $/, "$1")}
        </code>,
      );
    } else if (strongStar !== undefined || strongUnderscore !== undefined) {
      const body = (strongStar ?? strongUnderscore)!;
      out.push(
        <strong key={`${keyPrefix}-b-${key++}`} className="font-semibold">
          {inline(body, handles, `${keyPrefix}-b${key}`)}
        </strong>,
      );
    } else if (strike !== undefined) {
      out.push(
        <span key={`${keyPrefix}-s-${key++}`} className="line-through opacity-70">
          {inline(strike, handles, `${keyPrefix}-s${key}`)}
        </span>,
      );
    } else if (emStar !== undefined || emUnderscore !== undefined) {
      const body = (emStar ?? emUnderscore)!;
      out.push(
        <em key={`${keyPrefix}-i-${key++}`} className="italic">
          {inline(body, handles, `${keyPrefix}-i${key}`)}
        </em>,
      );
    } else if (linkHref !== undefined) {
      const href = safeHref(linkHref);
      if (href) {
        out.push(
          <InlineLink key={`${keyPrefix}-l-${key++}`} href={href}>
            {inline(linkText || href, handles, `${keyPrefix}-l${key}`)}
          </InlineLink>,
        );
      } else {
        plain(whole);
      }
    } else {
      const raw = (autoAngle ?? autoBare)!;
      const href = safeHref(raw);
      if (href) {
        out.push(
          <InlineLink key={`${keyPrefix}-a-${key++}`} href={href}>
            {raw}
          </InlineLink>,
        );
      } else {
        plain(whole);
      }
    }
  }

  plain(text.slice(cursor));
  return out;
}

/* -------------------------------------------------------------------- blocks */

const HEADING_CLASS: Record<number, string> = {
  1: "text-[15px] font-semibold",
  2: "text-[14px] font-semibold",
  3: "text-[13.5px] font-semibold",
  4: "text-[13px] font-semibold",
  5: "text-[13px] font-medium",
  6: "text-[12.5px] font-medium uppercase tracking-wide text-muted-foreground",
};

/**
 * The checkboxes of one body: which drawn index each box is, and
 * what ticking one does. Without `onToggle` they are drawn but disabled — an
 * agent's comment is a report, not a form.
 */
interface Checks {
  indexOf: Map<TaskMark, number>;
  onToggle?: (index: number) => void;
}

const NO_CHECKS: Checks = { indexOf: new Map() };

function TaskBox({ mark, checks }: { mark: TaskMark; checks: Checks }) {
  const index = checks.indexOf.get(mark) ?? -1;
  const { onToggle } = checks;
  return (
    <input
      type="checkbox"
      checked={mark.checked}
      disabled={!onToggle}
      onChange={() => onToggle?.(index)}
      aria-label={mark.checked ? "Done; untick" : "Not done; tick"}
      className="mt-[0.3em] size-3.5 shrink-0 accent-primary enabled:cursor-pointer disabled:opacity-70"
    />
  );
}

function renderBlocks(blocks: Block[], handles: Set<string>, keyPrefix: string, checks: Checks = NO_CHECKS): ReactNode[] {
  return blocks.map((block, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (block.type) {
      case "code":
        return (
          // Its own scroll container: a stack trace or a wide diff must not be
          // what makes the dialog scroll sideways.
          <pre
            key={key}
            className="overflow-x-auto rounded-md border border-border/70 bg-muted/60 p-2 scrollbar-slim"
          >
            <code className="font-mono text-[12px] leading-relaxed text-card-foreground">{block.code}</code>
          </pre>
        );
      case "heading": {
        const Tag = `h${Math.min(block.level + 2, 6)}` as "h3";
        return (
          <Tag key={key} className={HEADING_CLASS[block.level] ?? HEADING_CLASS[3]}>
            {inline(block.text, handles, key)}
          </Tag>
        );
      }
      case "hr":
        return <hr key={key} className="border-border/70" />;
      case "quote":
        return (
          <blockquote key={key} className="space-y-1.5 border-l-2 border-border pl-2.5 text-muted-foreground">
            {/* No handles: a quoted @name is not a request in core either. */}
            {renderBlocks(block.children, NO_HANDLES, key, checks)}
          </blockquote>
        );
      case "list": {
        const Tag = block.ordered ? "ol" : "ul";
        return (
          <Tag
            key={key}
            start={block.ordered ? block.start : undefined}
            className={cn(
              "space-y-1 pl-5",
              block.ordered ? "list-decimal marker:text-muted-foreground" : "list-disc marker:text-muted-foreground",
            )}
          >
            {block.items.map((item, itemIndex) => {
              const mark = block.tasks[itemIndex];
              // A task item drops its bullet for the box, pulled back into the
              // list's indent so the box sits where the bullet would have.
              return mark ? (
                <li key={`${key}-i${itemIndex}`} className="-ml-5 flex list-none items-start gap-2">
                  <TaskBox mark={mark} checks={checks} />
                  <div className="min-w-0 flex-1 space-y-1.5">{renderBlocks(item, handles, `${key}-i${itemIndex}`, checks)}</div>
                </li>
              ) : (
                <li key={`${key}-i${itemIndex}`} className="space-y-1.5">
                  {renderBlocks(item, handles, `${key}-i${itemIndex}`, checks)}
                </li>
              );
            })}
          </Tag>
        );
      }
      case "table":
        return (
          <div key={key} className="overflow-x-auto scrollbar-slim">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr>
                  {block.header.map((cell, cellIndex) => (
                    <th
                      key={`${key}-h${cellIndex}`}
                      className="border border-border/70 bg-muted/50 px-1.5 py-1 text-left font-medium"
                    >
                      {inline(cell, handles, `${key}-h${cellIndex}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={`${key}-r${rowIndex}`}>
                    {block.header.map((_, cellIndex) => (
                      <td key={`${key}-r${rowIndex}c${cellIndex}`} className="border border-border/70 px-1.5 py-1 align-top">
                        {inline(row[cellIndex] ?? "", handles, `${key}-r${rowIndex}c${cellIndex}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return (
          <p key={key} className="break-words">
            {inline(block.text, handles, key)}
          </p>
        );
    }
  });
}

/**
 * A markdown body, with `@mentions` highlighted the same way plain text is.
 * `onToggle` makes its task-list boxes tickable, called with the box's index in
 * drawn order — which `toggleTaskListItem` (lib/task-list.ts) takes back to the
 * one line it came from.
 */
export function Markdown({
  text,
  handles,
  className,
  onToggle,
}: {
  text: string;
  handles: Set<string>;
  className?: string;
  onToggle?: (index: number) => void;
}) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  const indexOf = useMemo(() => new Map(taskMarks(blocks).map((mark, index) => [mark, index])), [blocks]);
  const checks = useMemo(() => ({ indexOf, onToggle }), [indexOf, onToggle]);
  return <div className={cn("space-y-2 break-words", className)}>{renderBlocks(blocks, handles, "b", checks)}</div>;
}
